import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import Icon from '../../../components/AppIcon';


/**
 * Workflow Builder Component
 * Allows users to add, remove, and configure practice tools in their workflow
 */
export default function WorkflowBuilder({
  availableTools,
  customWorkflow,
  onAddTool,
  onRemoveTool,
  onUpdateSegment,
  onReorder
}) {
  
  const getToolConfig = (toolId) => {
    return availableTools?.find(t => t?.id === toolId);
  };

  const handleDurationChange = (segmentId, newDuration) => {
    onUpdateSegment(segmentId, { duration: parseInt(newDuration) || 5 });
  };

  const handleConfigChange = (segmentId, configKey, value) => {
    const segment = customWorkflow?.find(s => s?.id === segmentId);
    const newConfig = { ...(segment?.config || {}), [configKey]: value };
    onUpdateSegment(segmentId, { config: newConfig });
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4">Workflow Builder</h2>

      {/* Available Tools */}
      <div className="mb-6">
        <h3 className="text-white/80 text-sm font-medium mb-3">Add Practice Tools</h3>
        <div className="grid grid-cols-3 gap-3">
          {availableTools?.map((tool) => {
            const Icon = tool?.icon;
            return (
              <button
                key={tool?.id}
                onClick={() => onAddTool(tool)}
                className={`p-4 rounded-xl bg-gradient-to-r ${tool?.color} hover:opacity-90 transition-opacity text-white`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium text-center">{tool?.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Workflow Segments */}
      <div className="space-y-3">
        <h3 className="text-white/80 text-sm font-medium">Your Practice Sequence</h3>
        
        {customWorkflow?.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-8 text-center">
            <Plus className="w-12 h-12 text-white/40 mx-auto mb-3" />
            <p className="text-white/60 text-sm">
              Add tools above to build your practice workflow
            </p>
          </div>
        ) : (
          customWorkflow?.map((segment, index) => {
            const toolConfig = getToolConfig(segment?.tool);
            const Icon = toolConfig?.icon;
            
            return (
              <div
                key={segment?.id}
                className={`bg-gradient-to-r ${toolConfig?.color} rounded-xl p-4 text-white`}
              >
                <div className="flex items-start gap-3">
                  {/* Drag Handle */}
                  <div className="cursor-move pt-1">
                    <GripVertical className="w-5 h-5 text-white/60" />
                  </div>

                  {/* Tool Icon */}
                  <div className="pt-1">
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Configuration */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white/60 text-sm">#{index + 1}</span>
                        <h4 className="font-bold">{toolConfig?.name}</h4>
                      </div>
                      <button
                        onClick={() => onRemoveTool(segment?.id)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Duration Input */}
                    <div className="flex items-center gap-3 mb-3">
                      <label className="text-sm text-white/80">Duration:</label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={segment?.duration || 5}
                        onChange={(e) => handleDurationChange(segment?.id, e?.target?.value)}
                        className="w-20 px-3 py-1 bg-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                      />
                      <span className="text-sm text-white/80">minutes</span>
                    </div>

                    {/* Tool-Specific Configuration */}
                    {segment?.tool === 'breath-coach' && (
                      <div>
                        <label className="text-sm text-white/80 block mb-2">Breathing Pattern:</label>
                        <select
                          value={segment?.config?.preset || toolConfig?.presets?.[0]?.name}
                          onChange={(e) => handleConfigChange(segment?.id, 'preset', e?.target?.value)}
                          className="w-full px-3 py-2 bg-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                        >
                          {toolConfig?.presets?.map((preset) => (
                            <option key={preset?.name} value={preset?.name}>
                              {preset?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {segment?.tool === 'pitch-coach' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-white/80 block mb-2">Vocal Style:</label>
                          <select
                            value={segment?.config?.vocalStyle || 'Mix'}
                            onChange={(e) => handleConfigChange(segment?.id, 'vocalStyle', e?.target?.value)}
                            className="w-full px-3 py-2 bg-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                          >
                            {toolConfig?.vocalStyles?.map((style) => (
                              <option key={style} value={style}>{style}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm text-white/80 block mb-2">Practice Mode:</label>
                          <select
                            value={segment?.config?.mode || 'Melody Matching'}
                            onChange={(e) => handleConfigChange(segment?.id, 'mode', e?.target?.value)}
                            className="w-full px-3 py-2 bg-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                          >
                            {toolConfig?.practiceModes?.map((mode) => (
                              <option key={mode} value={mode}>{mode}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {segment?.tool === 'audition-songs' && (
                      <div>
                        <label className="text-sm text-white/80 block mb-2">Recommendation Type:</label>
                        <select
                          value={segment?.config?.recommendationType || 'current-role'}
                          onChange={(e) => handleConfigChange(segment?.id, 'recommendationType', e?.target?.value)}
                          className="w-full px-3 py-2 bg-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                        >
                          <option value="current-role">Current Role Focus</option>
                          <option value="skill-building">Skill Building</option>
                          <option value="performance-ready">Performance Ready</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}